import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ClipboardCheck, CircleCheck, CircleAlert, CircleSlash, Mail, Send, X } from "lucide-react";
import { API } from "../../context/AuthContext";

const STATUS = {
  verified: { Icon: CircleCheck, cls: "text-emerald-400", label: "Verified" },
  missing: { Icon: CircleAlert, cls: "text-rose-400", label: "Missing" },
  bypassed: { Icon: CircleSlash, cls: "text-gray-500", label: "Not necessary" },
};

export default function GapAudit({ caseId, canEdit }) {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState(null);
  const [drafting, setDrafting] = useState(false);
  const [toEmail, setToEmail] = useState("");
  const [sending, setSending] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/gap-audit`, { params: { case_id: caseId } })
      .then((res) => setData(res.data))
      .catch(() => {});
  }, [caseId]);

  useEffect(() => { load(); setDraft(null); }, [load]);

  const toggle = async (item) => {
    const bypassed = item.status !== "bypassed";
    try {
      await axios.post(`${API}/gap-audit/bypass`, { case_id: caseId, item_id: item.item_id, bypassed });
      load();
    } catch {
      toast.error("Couldn't update that item.");
    }
  };

  const makeDraft = async () => {
    setDrafting(true);
    try {
      const res = await axios.post(`${API}/gap-audit/email-draft`, { case_id: caseId });
      setDraft(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't draft the email.");
    } finally {
      setDrafting(false);
    }
  };

  const send = async () => {
    setSending(true);
    try {
      await axios.post(`${API}/gap-audit/email-send`, { case_id: caseId, to_email: toEmail, subject: draft.subject, body: draft.body });
      toast.success(`Outreach email sent to ${toEmail}!`);
      setDraft(null);
      setToEmail("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed.");
    } finally {
      setSending(false);
    }
  };

  if (!data) return null;

  return (
    <div className="bg-[#131f3a] border border-white/5 rounded-2xl p-5 space-y-3" data-testid="gap-audit-panel">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="text-sm font-bold text-white uppercase tracking-wide font-display flex items-center gap-2">
          <ClipboardCheck className="w-4 h-4 text-[#2dd4bf]" /> Automated Gap Audit
        </h3>
        <span className={`text-[10px] font-mono font-bold px-2 py-0.5 rounded border ${data.missing_count > 0 ? "text-rose-400 border-rose-500/40 bg-rose-500/10" : "text-emerald-400 border-emerald-500/40 bg-emerald-500/10"}`} data-testid="gap-missing-count">
          {data.missing_count > 0 ? `${data.missing_count} MISSING` : "FILE COMPLETE"}
        </span>
      </div>

      <div className="space-y-1">
        {data.items.map((item) => {
          const s = STATUS[item.status];
          return (
            <div key={item.item_id} className="flex items-center justify-between gap-2 border-b border-white/5 pb-1.5" data-testid={`gap-item-${item.item_id}`}>
              <div className="flex items-center gap-2 min-w-0">
                <s.Icon className={`w-3.5 h-3.5 shrink-0 ${s.cls}`} />
                <span className={`text-[11px] truncate ${item.status === "bypassed" ? "text-gray-500 line-through" : "text-gray-200"}`}>{item.label}</span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className={`text-[9px] font-mono uppercase ${s.cls}`}>{s.label}</span>
                {canEdit && item.status !== "verified" && (
                  <button onClick={() => toggle(item)} data-testid={`gap-bypass-${item.item_id}`} className="text-[9px] font-mono text-gray-400 hover:text-white border border-white/10 px-1.5 py-0.5 rounded transition-colors duration-200">
                    {item.status === "bypassed" ? "REQUIRE" : "MARK NOT NECESSARY"}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {canEdit && data.missing_count > 0 && !draft && (
        <button onClick={makeDraft} disabled={drafting} data-testid="gap-draft-email-btn" className="w-full flex items-center justify-center gap-1.5 bg-[#2dd4bf] hover:bg-[#5eead4] disabled:opacity-60 text-gray-950 font-bold text-[11px] font-mono py-2 rounded-lg transition-colors duration-200">
          <Mail className="w-3.5 h-3.5" /> {drafting ? "DRAFTING..." : "DRAFT CLIENT OUTREACH EMAIL"}
        </button>
      )}

      {draft && (
        <div className="space-y-2 border border-[#2dd4bf]/20 rounded-xl p-3 bg-gray-950" data-testid="gap-email-draft">
          <div className="flex justify-between items-center">
            <span className="text-[10px] font-mono font-bold text-[#2dd4bf] uppercase">Auto-drafted client email — review & send</span>
            <button onClick={() => setDraft(null)} data-testid="gap-draft-close" className="text-gray-500 hover:text-white"><X className="w-3.5 h-3.5" /></button>
          </div>
          <input value={draft.subject} onChange={(e) => setDraft({ ...draft, subject: e.target.value })} data-testid="gap-email-subject" className="w-full bg-[#131f3a] border border-white/10 rounded-lg px-2.5 py-1.5 text-[11px] text-gray-100 focus:outline-none focus:border-[#2dd4bf]/50" />
          <textarea value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} rows={8} data-testid="gap-email-body" className="w-full bg-[#131f3a] border border-white/10 rounded-lg px-2.5 py-1.5 text-[11px] text-gray-200 font-mono leading-relaxed focus:outline-none focus:border-[#2dd4bf]/50 resize-y" />
          <div className="flex gap-2">
            <input value={toEmail} onChange={(e) => setToEmail(e.target.value)} placeholder="client@email.com" type="email" data-testid="gap-email-to" className="flex-1 bg-[#131f3a] border border-white/10 rounded-lg px-2.5 py-1.5 text-[11px] text-gray-100 focus:outline-none focus:border-[#2dd4bf]/50" />
            <button onClick={send} disabled={sending || !toEmail} data-testid="gap-email-send-btn" className="flex items-center gap-1.5 bg-[#f59e0b] hover:bg-[#fbbf24] disabled:opacity-50 text-gray-950 font-bold text-[10px] font-mono px-4 py-1.5 rounded-lg transition-colors duration-200">
              <Send className="w-3 h-3" /> {sending ? "SENDING..." : "SEND"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
